/**
 * Tracking Number Formatter
 * 
 * Formats tracking numbers according to courier-specific display conventions.
 * Currently supports FedEx tracking number formatting with spaces.
 */

export type CourierType = 
  | 'FedEx' 
  | 'UPS' 
  | 'DHL' 
  | 'CEVA' 
  | 'ABF Freight' 
  | 'DB Schenker'
  | 'Day & Ross'
  | string; // Allow other courier names

/**
 * Format a tracking number based on courier-specific conventions
 * 
 * @param tracking - The raw tracking number string
 * @param courier - The courier/carrier name
 * @returns Formatted tracking number
 * 
 * @example
 * ```typescript
 * formatTracking('123456789012', 'FedEx')
 * // Returns: "1234 5678 9012"
 * 
 * formatTracking('1Z999AA10123456784', 'UPS')
 * // Returns: "1Z999AA10123456784" (no formatting)
 * ```
 */
export function formatTracking(tracking: string, courier: CourierType): string {
  if (!tracking) {
    return '';
  }

  // FedEx: Add spaces after every 4 digits (XXXX XXXX XXXX)
  if (courier === 'FedEx') {
    return tracking.replace(/^(.{4})(.{4})(.*)$/, "$1 $2 $3");
  }

  // Future: Add UPS, DHL, etc. formatting if needed
  return tracking;
}

/**
 * Remove formatting from a tracking number (returns raw digits/characters)
 * Useful for API calls or database queries
 * 
 * @param formattedTracking - Formatted tracking number with spaces/dashes
 * @returns Raw tracking number without formatting
 * 
 * @example
 * ```typescript
 * unformatTracking('1234 5678 9012')
 * // Returns: "123456789012"
 * ```
 */
export function unformatTracking(formattedTracking: string): string {
  return formattedTracking.replace(/[\s-]/g, '');
}
